import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { useLocation } from 'react-router-dom';
import { APIContext } from '../../Contexts/APIContext';
import DataContainer from '../../Utilities/DataContainer';
import RenderCheckBoxField from '../../Utilities/Forms/RenderCheckBoxField';
import RenderSelectField from '../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../Utilities/Forms/RenderTextField';
import yesNoDropdown, { yesNoDropdownBool } from '../../Utilities/Constants/YesNoDropdown';
import ParkingDetailsViewModel from '../ViewModels/ParkingDetailsViewModel';
import PDFLink from '../../Popups/PDFLink';
import EditControl from './EditControl';
import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';

const AreaParkingRestrictionsDropDown = [
    { id: 'NO', label: 'No' },
    { id: 'YS', label: 'Yes, Some Of The Area' },
    { id: 'YM', label: 'Yes, Most Of The Area' }
];

const OffStreetParkingDropDown = [
    { id: 'NO', label: 'No' },
    { id: 'YA', label: 'Yes, Public Off-Street Parking is Available' },
    { id: 'YU', label: 'Yes, in Unofficial or Unspecified Areas (e.g. Waste Ground, Verges)' },
    { id: 'YE', label: 'Yes, Additional Off-Site Parking is Provided by The Employer' }
];
const OffStreetParkingChargesDropDown = [
    { id: 'NO', label: 'No' },
    { id: 'YS', label: 'Yes, At Specific Times Of The Day' },
    { id: 'YA', label: 'Yes, All Day' }
];

const CPZPermittedDropDown = [
    { id: 'NO', label: 'No' },
    { id: 'YA', label: 'Yes, All Day' },
    { id: 'YS', label: 'Yes, At Special Times Of The Day' }
];

const SurfaceTypeDropDown = [
    { id: 'N', label: 'Non-Surface' },
    { id: 'S', label: 'Surface' },
];

const ChargePeriodDropDown = [
    { id: 'H', label: 'Hour' },
    { id: 'D', label: 'Day' },
    { id: 'M', label: 'Month' },
    { id: 'Y', label: 'Year' },
];

const parkingNumberFields: (keyof ParkingDetailsViewModel)[] = [
    'total',
    'employeeParking',
    'disabledParking',
    'visitorParking',
    'ogvParking',
    'ogvLoadingBays',
    'cycleRacks',
    'parentToddler',
    'motorcycleSpaces',
    'electricChargingBays',
    'onStreetSpaces',
    'drivewaySpaces',
    'garageSpaces',
    'communalSpaces',
    'allocatedSpaces',
    'onStreetParkingCharges',
    'onStreetMaxParkingDuration',
    'offStreetParkingChargesAmount',
    'offStreetParkingApproxSpaces',
    'parkAndRideJourney',
    'totPar1Calc',
    'totPar2Calc',
    'timeLimitMinutes',
    'minChargePerHour',
    'publicCarParksChargedSpaces',
    'publicCarParksFreeSpaces',
    'publicCarParksTotalSpaces',
    'publicCarParksMinHourlyCharge',
    'otherDevelopmentsChargedSpaces',
    'otherDevelopmentsFreeSpaces',
    'otherDevelopmentsTotalSpaces',
    'otherDevelopmentsMinHourlyCharge',
    'offSiteProvidedChargedSpaces',
    'offSiteProvidedFreeSpaces',
    'offSiteProvidedTotalSpaces',
    'offSiteProvidedMinHourlyCharge',
    'parkRideChargedSpaces',
    'parkRideFreeSpaces',
    'parkRideTotalSpaces',
    'parkRideMinHourlyCharge',
    'otherOffSiteChargedSpaces',
    'otherOffSiteFreeSpaces',
    'otherOffSiteTotalSpaces',
    'otherOffSiteMinHourlyCharge',
];

const normaliseParkingNumbers = (form: ParkingDetailsViewModel): ParkingDetailsViewModel => {
    const payload = { ...form };

    parkingNumberFields.forEach((field) => {
        const value = payload[field];
        if (typeof value !== 'string') return;

        (payload as any)[field] = value.trim() === '' ? null : Number(value);
    });

    return payload;
};

interface IProps {
    hasParkingDetails?: boolean | null;
    reloadSite?: () => void;
}

export default function Parking({ hasParkingDetails, reloadSite }: IProps) {
    const { get, post, showMessage } = useContext(APIContext);
    const [parking, setParking] = useState<ParkingDetailsViewModel>(new ParkingDetailsViewModel());

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [readOnly, setReadOnly] = useState(true);
    const [creating, setCreating] = useState(false);

    const siteRef = params.get('siteRef') || '';

    const landUseKey = siteRef.split('-')[1] ?? "";
    const sublandUseKey = siteRef.split('-')[2] ?? "";

    const formContext = useForm<ParkingDetailsViewModel>({
        defaultValues: new ParkingDetailsViewModel(),
    });

    const { reset, watch } = formContext;

    useEffect(() => {
        if (siteRef && hasParkingDetails !== false) {
            getParkingDetailsForSite();
        }
    }, [siteRef, hasParkingDetails]);

    useEffect(() => {
        reset(parking);
    }, [parking, reset]);

    const getParkingDetailsForSite = () => {
        get('ParkingDetails', `GetParkingDetailsForSite?reference=${siteRef}`, setParking)
    };

    const createParking = async () => {
        setCreating(true);
        await post('ParkingDetails', `CreateParking?reference=${siteRef}`, {}, () => {
            showMessage('success', 'Parking details added successfully');
            getParkingDetailsForSite();
            reloadSite?.();
        });
        setCreating(false);
    };

    const submit = (form: ParkingDetailsViewModel) => {
        post('ParkingDetails', `UpdateParking?reference=${siteRef}`, normaliseParkingNumbers(form), async () => {
            await getParkingDetailsForSite()
            showMessage('success', 'Parking updated successfully')
            setReadOnly(true);
        });
    }

    const isAfterCutoff = (dateValue: string | Date | undefined | null) => {
        if (!dateValue) return false;

        const inputDate = new Date(dateValue);
        const cutoffDate = new Date(2026, 2, 7);

        return inputDate > cutoffDate;
    };

    const hasOffSiteParking = () => {
        const disallowed = [
            { land: "02", sub: "H" },
            { land: "09", sub: "A" },
            { land: "09", sub: "B" },
            { land: "09", sub: "C" },
            { land: "09", sub: "D" },
            { land: "09", sub: "E" },
            { land: "09", sub: "F" },
            { land: "09", sub: "G" },
            { land: "09", sub: "H" },
            { land: "11", sub: "A" },
            { land: "15", sub: "A" },
            { land: "15", sub: "B" },
            { land: "15", sub: "D" },
            { land: "16", sub: "C" }
        ];

        const match = disallowed.some(x => x.land === landUseKey && x.sub === sublandUseKey);

        return !match;
    };


    const parkingDetails = watch();
    const showVersion2 = isAfterCutoff(parkingDetails?.created);
    const showOffsiteParking = hasOffSiteParking();
    const currency2DpFormat = (value: string | number | null | undefined): string => {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        const parsed = typeof value === "number" ? value : Number(value);
        if (Number.isNaN(parsed)) {
            return String(value);
        }
        return parsed.toFixed(2);
    };

    if (hasParkingDetails === false) {
        return <>
            <Typography sx={{ p: 2 }}>
                {creating ? 'Adding parking details...' : 'No parking details are available. Enter edit mode, then select Add.'}
            </Typography>
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} showSave={false} />
            {!readOnly && (
                <Tooltip title="Add parking details">
                    <Fab
                        color="primary"
                        disabled={creating}
                        onClick={createParking}
                        sx={{ position: 'absolute', bottom: 16, right: 16 }}
                    >
                        <AddIcon />
                    </Fab>
                </Tooltip>
            )}
        </>;
    }

    return (
        <FormContainer formContext={formContext} onSuccess={submit}>
            <Grid container alignItems="flex-start">
                <Grid size={4}>
                    <Stack spacing={2}>
                        <Grid container alignItems="flex-start">
                            <DataContainer title="On-Site Parking Details">
                                <Stack spacing={1}>
                                    <RenderCheckBoxField
                                        name={'noOnSiteParking'}
                                        label={"This site has no on-site parking"}
                                        readOnly={readOnly}
                                        alwaysShow={true}
                                    />
                                    <RenderTextField name={'total'} label={"Total"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                    {parkingDetails?.totPar1Label && (
                                        <RenderTextField name={'totPar1Value'} label={parkingDetails?.totPar1Label} readOnly={readOnly} />
                                    )}

                                    {parkingDetails?.totPar2Label && (
                                        <RenderTextField name={'totPar2Value'} label={parkingDetails?.totPar2Label} readOnly={readOnly} />
                                    )}

                                    {(landUseKey === '03' && sublandUseKey !== 'J') ? (
                                        <>
                                            <RenderTextField name={'onStreetSpaces'} label={"On - Street"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'drivewaySpaces'} label={"Driveway"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'garageSpaces'} label={"Garages"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'communalSpaces'} label={"Communal Parking Spaces"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'allocatedSpaces'} label={"Allocated Spaces"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                        </>
                                    ) : (
                                        <>
                                            <RenderTextField name={'employeeParking'} label={"Employee"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'disabledParking'} label={"Disabled"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'visitorParking'} label={"Visitor/Customer"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'ogvParking'} label={"OGV Parking Bays"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'ogvLoadingBays'} label={"OGV Loading Bays"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'cycleRacks'} label={"Cycle Racks"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'parentToddler'} label={"Parent and Toddler"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderTextField name={'motorcycleSpaces'} label={"Motorcycle Spaces"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                            <RenderSelectField name={'parkingCharges'} label={"Parking Charges"} readOnly={readOnly} options={yesNoDropdown} />
                                        </>
                                    )}
                                    <RenderTextField name={'electricChargingBays'} label={"Electric Vehicle Charging Bays"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />

                                    {(landUseKey == '02' || landUseKey == '03') && (
                                        <RenderSelectField name={'carClub'} label={"Is this site associated with a Car Club?"} readOnly={readOnly} options={yesNoDropdown} />
                                    )}
                                    <RenderSelectField name={'siteParkingSurface'} label={"Site Parking Surface or Non-Surface"} readOnly={readOnly} options={SurfaceTypeDropDown} />

                                    {(landUseKey != '03') && (
                                        <RenderTextField name={'managementComments'} label={"Comments about the management of the site car park"} readOnly={readOnly} multiLine={true} rows={5} labelSize={12} inputSize={12} />
                                    )}
                                </Stack>
                            </DataContainer>
                        </Grid>
                    </Stack>
                </Grid>
                <Grid size={4}>
                    <Stack spacing={2}>

                        {(showVersion2) ? (
                            <>
                                {showOffsiteParking && (
                                    <DataContainer title="Off-Site Parking Summary">
                                        <Stack spacing={1}>
                                            <RenderSelectField name={'offSiteParkingPossible'} label={"Is any off-site parking of any type possible within a 1.2-kilometre walking distance of the site?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                            {(parkingDetails?.offSiteParkingPossible || !readOnly) && (
                                                <>
                                                    <RenderSelectField name={'freeWithoutRestrictions'} label={"If YES, is any of this parking available for free without any restrictions?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                                </>
                                            )}
                                            <RenderSelectField name={'includedInSurveyCounts'} label={"Has off-site parking activity been included in the survey counts?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                            <RenderTextField name={'offSiteAdditionalComments'} label={"Additional comments on off-site parking"} readOnly={readOnly} multiLine={true} rows={4} inputSize={12} labelSize={12} />
                                        </Stack>
                                    </DataContainer>
                                )}

                                <DataContainer title={"Local On-Street Parking"}>
                                    <Stack spacing={1}>
                                        <RenderSelectField name={'freeUnrestrictedAvailable'} label={"Is any free and unrestricted on-street parking available to non-residents within a 1.2-kilometre walking distance of the site?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                        <RenderSelectField name={'restrictionsWithinArea'} label={"Are there any areas of on-street parking restrictions/controls within a 1.2-kilometre walking distance of the site?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                        {(parkingDetails?.restrictionsWithinArea || !readOnly) && (
                                            <>
                                                <RenderSelectField name={'nonResidentPermitted'} label={"If YES, is on-street parking permitted for non-residents within any of these restricted areas?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                                {(parkingDetails?.nonResidentPermitted || !readOnly) && (
                                                    <>
                                                        <RenderSelectField name={'timeLimitExists'} label={"If YES, is there a time limit?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                                        {(parkingDetails?.timeLimitExists || !readOnly) && (
                                                            <RenderTextField name={'timeLimitMinutes'} label={"Minutes"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                                        )}
                                                        <RenderSelectField name={'parkingCharged'} label={"If YES, is such parking charged?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                                        {(parkingDetails?.parkingCharged || !readOnly) && (
                                                            <RenderTextField name={'minChargePerHour'} label={"Minimum charge per hour (\u00A3/\u20AC)"} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} />
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Stack>
                                </DataContainer>

                                <DataContainer title={"Local Off-Street Parking"}>
                                    <Stack spacing={1}>
                                        <RenderSelectField name={'offSiteParkingAvailableWithinWalkingDistance'} label={"Is any off-site off-street parking available within a 1.2-kilometre walking distance of the site?"} readOnly={readOnly} options={yesNoDropdownBool} />
                                        {(parkingDetails?.offSiteParkingAvailableWithinWalkingDistance || !readOnly) && (
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid size={3}><Typography variant="body2">Type</Typography></Grid>
                                                <Grid size={2}><Typography variant="body2" align="center">Charged Spaces</Typography></Grid>
                                                <Grid size={2}><Typography variant="body2" align="center">Free Spaces</Typography></Grid>
                                                <Grid size={2}><Typography variant="body2" align="center">Total Spaces</Typography></Grid>
                                                <Grid size={3}><Typography variant="body2" align="center">Min Hourly Charge</Typography></Grid>

                                                <Grid size={3}><Typography variant="body2">Public Car Parks</Typography></Grid>
                                                <Grid size={2}><RenderTextField name={'publicCarParksChargedSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'publicCarParksFreeSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'publicCarParksTotalSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={3}><RenderTextField name={'publicCarParksMinHourlyCharge'} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} /></Grid>

                                                <Grid size={3}><Typography variant="body2">Other Developments Without Restrictions</Typography></Grid>
                                                <Grid size={2}><RenderTextField name={'otherDevelopmentsChargedSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'otherDevelopmentsFreeSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'otherDevelopmentsTotalSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={3}><RenderTextField name={'otherDevelopmentsMinHourlyCharge'} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} /></Grid>

                                                <Grid size={3}><Typography variant="body2">Off-Site Parking Provided by the Site</Typography></Grid>
                                                <Grid size={2}><RenderTextField name={'offSiteProvidedChargedSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'offSiteProvidedFreeSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'offSiteProvidedTotalSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={3}><RenderTextField name={'offSiteProvidedMinHourlyCharge'} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} /></Grid>

                                                <Grid size={3}><Typography variant="body2">Park & Ride Sites</Typography></Grid>
                                                <Grid size={2}><RenderTextField name={'parkRideChargedSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'parkRideFreeSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'parkRideTotalSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={3}><RenderTextField name={'parkRideMinHourlyCharge'} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} /></Grid>

                                                <Grid size={3}><Typography variant="body2">Other Off-Site Off-Street Parking</Typography></Grid>
                                                <Grid size={2}><RenderTextField name={'otherOffSiteChargedSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'otherOffSiteFreeSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={2}><RenderTextField name={'otherOffSiteTotalSpaces'} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} /></Grid>
                                                <Grid size={3}><RenderTextField name={'otherOffSiteMinHourlyCharge'} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} readOnlyFormat={currency2DpFormat} fixedDecimalScale={2} /></Grid>
                                            </Grid>
                                        )}
                                    </Stack>
                                </DataContainer>
                            </>
                        ) : (
                            <>
                                {showOffsiteParking && (
                                    <Grid container alignItems="flex-start">
                                        <DataContainer title="Off-Site Parking Details">
                                            <Stack spacing={1}>
                                                <RenderSelectField name={'offSiteParkingAvailable'} label={"Is there off-site parking available"} readOnly={readOnly} options={yesNoDropdown} />
                                                <RenderSelectField name={'offSiteParkingIncludedInCount'} label={"Off-Site Parking included in the counts"} readOnly={readOnly} options={yesNoDropdown} />
                                                <RenderSelectField name={'freeOnStreetParkingAvailable'} label={"Free On-Street Parking Available Nearby"} readOnly={readOnly} options={yesNoDropdown} />

                                                {parkingDetails?.freeOnStreetParkingAvailable != "N" &&
                                                    (<RenderSelectField name={'freeOnStreetParkingEasyToFindSpace'} label={"If yes, considered easy to find a space"} readOnly={readOnly} options={yesNoDropdown} />)
                                                }
                                                <RenderSelectField name={'preparedToPayEasyToFindSpace'} label={"If prepared to pay, easy to find somewhere to park off-site all day"} readOnly={readOnly} options={yesNoDropdown} />
                                            </Stack>

                                        </DataContainer>
                                    </Grid>
                                )}

                                <Grid container alignItems="flex-start">
                                    <DataContainer title="Parking Restrictions">
                                        <RenderSelectField name={'parkingRestrictions'} label={"Area subject to parking restrictions (CPZ)"} readOnly={readOnly} options={AreaParkingRestrictionsDropDown} />
                                        {
                                            parkingDetails?.parkingRestrictions !== "NO" && (
                                                <>

                                                    <Grid size={8}>
                                                        <Typography variant="h6">Details on Parking Restrictions</Typography>
                                                    </Grid>
                                                    <RenderSelectField name={'parkingRestrictionsCPZNonResident'} label={"Permitted on-street parking for non-residents available within this CPZ"} readOnly={readOnly} options={CPZPermittedDropDown} />
                                                    <RenderSelectField name={'parkingRestrictionsTimeLimited'} label={"If yes, time limited for non-residents"} readOnly={readOnly} options={yesNoDropdown} />
                                                    {
                                                        parkingDetails?.parkingRestrictionsCPZNonResident !== "NO" && (
                                                            <>
                                                                <Grid size={12}>
                                                                    <Typography variant="h6">Details about On-Street Parking for this CPZ</Typography>
                                                                </Grid>
                                                                <RenderSelectField name={'onStreetParkingWithinCPZ'} label={"Charges for non-residents parking if permitted"} readOnly={readOnly} options={CPZPermittedDropDown} />
                                                                {
                                                                    parkingDetails?.onStreetParkingWithinCPZ !== "NO" && (
                                                                        <>
                                                                            <RenderTextField name={'onStreetParkingCharges'} label={"Average Charge Per Hour"} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} fixedDecimalScale={2} />
                                                                            <RenderTextField name={'onStreetMaxParkingDuration'} label={"Maximum Parking Duration"} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} />
                                                                        </>
                                                                    )
                                                                }
                                                            </>
                                                        )
                                                    }
                                                </>
                                            )
                                        }
                                    </DataContainer>
                                </Grid>


                                <Grid container alignItems="flex-start">
                                    <DataContainer title="Off-Street Parking Available">
                                        <RenderSelectField name={'offStreetParkingAvailable'} readOnly={readOnly} options={OffStreetParkingDropDown} label={''} inputSize={12} />
                                        {
                                            parkingDetails?.offStreetParkingAvailable !== null && parkingDetails?.offStreetParkingAvailable !== "NO" && (
                                                <>
                                                    <Grid size={8}>
                                                        <Typography variant="h6">Details About Off-Street Parking</Typography>
                                                    </Grid>
                                                    <RenderTextField name={'offStreetParkingApproxSpaces'} label={"Approx. Available Spaces"} readOnly={readOnly} type="number" integerOnly min={0} inputProps={{ step: 1 }} />
                                                    <RenderSelectField name={'offStreetParkingWithinCPZ'} readOnly={readOnly} options={yesNoDropdown} label={'Parking Located Within a Control Parking Zone (CPZ)'} />
                                                    <RenderSelectField name={'offStreetParkingCharges'} readOnly={readOnly} options={OffStreetParkingChargesDropDown} label={'Charges For This Off-Street Parking'} />
                                                    {
                                                        parkingDetails?.offStreetParkingCharges !== "NO" && (
                                                            <>
                                                                <Grid size={8}>
                                                                    <Typography variant="h6">Details On Charges</Typography>
                                                                </Grid>
                                                                <RenderTextField name={'offStreetParkingChargesAmount'} label={"Charge Amount"} readOnly={readOnly} type="number" min={0} inputProps={{ inputMode: "decimal", step: 0.01 }} fixedDecimalScale={2} />
                                                                <RenderSelectField name={'offStreetParkingPeriod'} label={"Charge Period"} readOnly={readOnly} options={ChargePeriodDropDown} />
                                                            </>
                                                        )
                                                    }
                                                </>
                                            )
                                        }
                                    </DataContainer>
                                </Grid>
                            </>
                        )}
                    </Stack>
                </Grid>

                <Grid size={4}>
                    <Stack spacing={2}>
                        <Grid container alignItems="flex-start">
                            <DataContainer title="Types of servicing vehicle parking taking place">
                                <RenderCheckBoxField label={'on-site (internal, within specified bays or otherwise)'} name={'serviceVehiclesParkWithinSite'} readOnly={readOnly} />
                                <RenderCheckBoxField label={'off-site (on-street, in designated loading/servicing bays)'} name={'serviceVehiclesParkOutsideSiteDesignatedAreas'} readOnly={readOnly} />
                                <RenderCheckBoxField label={'off-site (in restricted areas e.g. double yellow lines)'} name={'serviceVehiclesParkOutsideSiteRestrictedAreas'} readOnly={readOnly} />
                            </DataContainer>
                        </Grid>

                        <DataContainer title="Park & Ride">
                            <Grid container alignItems="flex-start">
                                <RenderSelectField
                                    name={'parkAndRide'}
                                    label={"Park and Ride Type Facility providing relevant means of accessing the site"}
                                    inputSize={12}
                                    readOnly={readOnly}
                                    options={yesNoDropdown}
                                />
                                {(parkingDetails?.parkAndRide === "Y" || !readOnly) && (
                                    <>
                                        <Grid size={12}>
                                            <Typography variant="h6">Park and Ride Type Details</Typography>
                                        </Grid>
                                        <RenderTextField
                                            name={'parkAndRideJourney'}
                                            label={"Approximate Journey Time from the Facility to the Site"}
                                            readOnly={readOnly}
                                            type="number"
                                            min={0}
                                            inputProps={{ inputMode: "decimal", step: 0.01 }}
                                        />
                                    </>
                                )}
                                <PDFLink container="GeneralDocs" label="Explanatory note on the TRICS Parking Details Section" file="Parking Note" />
                            </Grid>
                        </DataContainer>
                        <DataContainer title="General Comments on Parking">
                            <Grid container alignItems="flex-start" size={12}>
                                <RenderTextField name={'generalComments'} multiLine={true} rows={5} readOnly={readOnly} inputSize={12} labelSize={12} />
                            </Grid>
                        </DataContainer>
                    </Stack>
                </Grid >
            </Grid >
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} />
        </FormContainer >
    );
}
